import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { hasCompanyAdminRole, getCurrentUser } from '../../utils/roleCheck';
import './AccountManagement.css';

function AccountManagementPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    AccountName: '',
    EmailId: '',
    Phone: '',
    BillingCode: '',
    BillingDept: '',
    CreditCard: '',
    Expiration: '',
    ThreeDigitCode: '',
    CardLast4: ''
  });
  
  // Delete confirmation
  const [deleteNumbers, setDeleteNumbers] = useState(['', '', '']);
  const [deleteError, setDeleteError] = useState(null);

  // Check authorization on mount
  useEffect(() => {
    const user = getCurrentUser();
    if (!hasCompanyAdminRole(user?.roles)) {
      navigate('/user-home');
    }
  }, [navigate]);

  // Load initial data
  useEffect(() => {
    loadAccounts();
  }, [currentPage, pageSize]);

  // Filter accounts based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAccounts(accounts);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = accounts.filter(account =>
        account.AccountName?.toLowerCase().includes(term) ||
        account.EmailId?.toLowerCase().includes(term) ||
        account.BillingCode?.toLowerCase().includes(term) ||
        account.BillingDept?.toLowerCase().includes(term)
      );
      setFilteredAccounts(filtered);
    }
  }, [searchTerm, accounts]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const user = getCurrentUser();
      const response = await apiService.listAccounts(currentPage, pageSize, user?.companyId);
      
      if (response.success) {
        setAccounts(response.accounts || []);
        setTotalCount(response.totalCount || 0);
        setTotalPages(Math.ceil((response.totalCount || 0) / pageSize));
      } else {
        setError('Failed to load accounts');
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(err.response?.data?.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = () => {
    setFormData({
      AccountName: '',
      EmailId: '',
      Phone: '',
      BillingCode: '',
      BillingDept: '',
      CreditCard: '',
      Expiration: '',
      ThreeDigitCode: '',
      CardLast4: ''
    });
    setShowCreateModal(true);
  };

  const handleEditAccount = async (account) => {
    try {
      console.log('Fetching account details for:', account.CompanyAccountId);
      const response = await apiService.getAccountDetails(account.CompanyAccountId);
      
      if (response && response.success && response.account) {
        const accountData = response.account;
        console.log('Account data:', accountData);
        setSelectedAccount(accountData);
        setFormData({
          AccountName: accountData.AccountName || '',
          EmailId: accountData.EmailId || '',
          Phone: accountData.Phone || '',
          BillingCode: accountData.BillingCode || '',
          BillingDept: accountData.BillingDept || '',
          CreditCard: '',  // Don't pre-fill for security
          Expiration: accountData.Expiration || '',
          ThreeDigitCode: '',  // Don't pre-fill for security
          CardLast4: accountData.CardLast4 || ''
        });
        setShowEditModal(true);
      } else {
        setError('Failed to load account details');
      }
    } catch (err) {
      console.error('Error loading account details:', err);
      setError('Failed to load account details');
    }
  };

  const handleDeleteAccount = async (account) => {
    try {
      // Check if account has users
      const response = await apiService.checkAccountUsers(account.CompanyAccountId);
      if (response.success && response.userCount > 0) {
        setError(`Cannot delete account: ${response.userCount} user(s) are attached to this account`);
        return;
      }
      
      setSelectedAccount(account);
      setDeleteNumbers(['', '', '']);
      setDeleteError(null);
      setShowDeleteModal(true);
    } catch (err) {
      console.error('Error checking account users:', err);
      setError('Failed to check account status');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-populate CardLast4 when CreditCard changes
      if (name === 'CreditCard' && value.length >= 4) {
        const cleanCard = value.replace(/\s/g, '');
        updated.CardLast4 = cleanCard.slice(-4);
      }
      
      return updated;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (showCreateModal) {
        const response = await apiService.createAccount(formData);
        if (response.success) {
          setSuccess('Account created successfully');
          setShowCreateModal(false);
          loadAccounts();
        }
      } else if (showEditModal) {
        const response = await apiService.updateAccount(selectedAccount.CompanyAccountId, formData);
        if (response.success) {
          setSuccess('Account updated successfully');
          setShowEditModal(false);
          loadAccounts();
        }
      }
    } catch (err) {
      console.error('Error saving account:', err);
      setError(err.response?.data?.message || 'Failed to save account');
    }
  };

  const handleConfirmDelete = async () => {
    // Validate three odd numbers
    const nums = deleteNumbers.map(n => parseInt(n));
    
    // Check if all are numbers
    if (nums.some(isNaN)) {
      setDeleteError('Please enter valid numbers');
      return;
    }
    
    // Check if all are odd
    if (!nums.every(n => n % 2 !== 0)) {
      setDeleteError('All three numbers must be odd');
      return;
    }
    
    // Check if all are different
    if (new Set(nums).size !== 3) {
      setDeleteError('All three numbers must be different');
      return;
    }
    
    try {
      const response = await apiService.deleteAccount(selectedAccount.CompanyAccountId);
      if (response.success) {
        setSuccess('Account deleted successfully');
        setDeleteError(null);
        setShowDeleteModal(false);
        loadAccounts();
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const maskCardNumber = (cardLast4) => {
    if (!cardLast4) return 'N/A';
    return `****${cardLast4}`;
  };

  return (
    <div className="account-management">
      <div className="account-management__header">
        <h1 className="account-management__title">Account Management</h1>
        <button 
          className="account-management__btn account-management__btn--primary"
          onClick={handleCreateAccount}
        >
          Create New Account
        </button>
      </div>

      {/* Success/Error messages */}
      {error && <div className="account-management__message account-management__message--error">{error}</div>}
      {success && <div className="account-management__message account-management__message--success">{success}</div>}

      {/* Search bar */}
      <div className="account-management__search">
        <input
          type="text"
          placeholder="Search accounts by name, email, billing code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="account-management__search-input"
        />
      </div>

      {/* Accounts grid */}
      {loading ? (
        <div className="account-management__loading">Loading accounts...</div>
      ) : (
        <>
          <div className="account-management__table-container">
            <table className="account-management__table">
              <thead>
                <tr>
                  <th>Account Name</th>
                  <th>Root Account</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Billing Code</th>
                  <th>Billing Dept</th>
                  <th>Card</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="account-management__no-data">No accounts found</td>
                  </tr>
                ) : (
                  filteredAccounts.map((account) => (
                    <tr key={account.CompanyAccountId}>
                      <td>{account.AccountName}</td>
                      <td>
                        <span className={`account-management__badge ${account.RootAccount ? 'account-management__badge--yes' : 'account-management__badge--no'}`}>
                          {account.RootAccount ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td>{account.EmailId}</td>
                      <td>{account.Phone || 'N/A'}</td>
                      <td>{account.BillingCode || 'N/A'}</td>
                      <td>{account.BillingDept || 'N/A'}</td>
                      <td>{maskCardNumber(account.CardLast4)}</td>
                      <td>{formatDate(account.CreateDate)}</td>
                      <td className="account-management__actions">
                        <button
                          onClick={() => handleEditAccount(account)}
                          className="account-management__btn account-management__btn--edit"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAccount(account)}
                          className="account-management__btn account-management__btn--delete"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="account-management__pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="account-management__btn account-management__btn--secondary"
              >
                Previous
              </button>
              <span className="account-management__page-info">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="account-management__btn account-management__btn--secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="account-management__modal-overlay" onClick={(e) => {
          if (e.target.className === 'account-management__modal-overlay') {
            setShowCreateModal(false);
            setShowEditModal(false);
          }
        }}>
          <div className="account-management__modal">
            <h2>{showCreateModal ? 'Create New Account' : 'Edit Account'}</h2>
            <form onSubmit={handleFormSubmit} className="account-management__form">
              <div className="account-management__form-group">
                <label>Account Name *</label>
                <input
                  type="text"
                  name="AccountName"
                  value={formData.AccountName}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="account-management__form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="EmailId"
                  value={formData.EmailId}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="account-management__form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="Phone"
                  value={formData.Phone}
                  onChange={handleFormChange}
                />
              </div>

              <div className="account-management__form-group">
                <label>Billing Code</label>
                <input
                  type="text"
                  name="BillingCode"
                  value={formData.BillingCode}
                  onChange={handleFormChange}
                />
              </div>

              <div className="account-management__form-group">
                <label>Billing Department</label>
                <input
                  type="text"
                  name="BillingDept"
                  value={formData.BillingDept}
                  onChange={handleFormChange}
                />
              </div>

              <div className="account-management__payment-section">
                <h3>Payment Information</h3>
                
                <div className="account-management__form-group">
                  <label>Credit Card {showEditModal && '(leave blank to keep existing)'}</label>
                  <input
                    type="text"
                    name="CreditCard"
                    value={formData.CreditCard}
                    onChange={handleFormChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                  />
                </div>

                <div className="account-management__form-row">
                  <div className="account-management__form-group">
                    <label>Expiration (MM/YY)</label>
                    <input
                      type="text"
                      name="Expiration"
                      value={formData.Expiration}
                      onChange={handleFormChange}
                      placeholder="12/25"
                      maxLength="5"
                    />
                  </div>

                  <div className="account-management__form-group">
                    <label>CVV {showEditModal && '(leave blank to keep existing)'}</label>
                    <input
                      type="password"
                      name="ThreeDigitCode"
                      value={formData.ThreeDigitCode}
                      onChange={handleFormChange}
                      placeholder="123"
                      maxLength="4"
                    />
                  </div>

                  <div className="account-management__form-group">
                    <label>Last 4 Digits</label>
                    <input
                      type="text"
                      name="CardLast4"
                      value={formData.CardLast4}
                      readOnly
                      className="account-management__readonly"
                    />
                  </div>
                </div>
              </div>

              <div className="account-management__form-actions">
                <button type="submit" className="account-management__btn account-management__btn--primary">
                  {showCreateModal ? 'Create Account' : 'Update Account'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                  className="account-management__btn account-management__btn--secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="account-management__modal-overlay" onClick={(e) => {
          if (e.target.className === 'account-management__modal-overlay') {
            setShowDeleteModal(false);
            setDeleteNumbers(['', '', '']);
            setDeleteError(null);
          }
        }}>
          <div className="account-management__modal account-management__modal--delete">
            <h2>Delete Account</h2>
            <p className="account-management__delete-warning">
              Are you sure you want to delete the account <strong>{selectedAccount?.AccountName}</strong>?
            </p>
            <p className="account-management__delete-instruction">
              Please enter three different odd numbers (1-99) to confirm deletion:
            </p>

            {deleteError && (
              <div className="account-management__message account-management__message--error">
                {deleteError}
              </div>
            )}

            <div className="account-management__delete-inputs">
              {[0, 1, 2].map((index) => (
                <input
                  key={index}
                  type="number"
                  min="1"
                  max="99"
                  value={deleteNumbers[index]}
                  onChange={(e) => {
                    const newNumbers = [...deleteNumbers];
                    newNumbers[index] = e.target.value;
                    setDeleteNumbers(newNumbers);
                  }}
                  className="account-management__delete-input"
                  placeholder={`Odd #${index + 1}`}
                />
              ))}
            </div>

            <div className="account-management__form-actions">
              <button
                onClick={handleConfirmDelete}
                className="account-management__btn account-management__btn--danger"
              >
                Confirm Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteNumbers(['', '', '']);
                  setDeleteError(null);
                }}
                className="account-management__btn account-management__btn--secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountManagementPage;
